import { Menu as MenuAtom } from 'module/ui/components/atoms/dropdown';
import { Loading } from 'module/ui/components/atoms/loading';
import { EditorPopoverProps } from 'module/ui/components/atoms/rich-text-editor/editor-popover/popover';
import { EditorPopover } from 'module/ui/components/atoms/rich-text-editor/editor-popover/popover';
import { Editor, Range } from '@tiptap/react';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { flatMap } from 'lodash';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { CommandPickerGroupItem, CommandPickerItem } from '../types';

interface CommandsPickerProps extends Pick<
  EditorPopoverProps,
  | 'popoverContentContainerClassName'
  | 'placement'
  | 'canIgnoreOutsideClickFor'
  | 'popoverState'
  | 'hidePopover'
> {
  editor: Editor;
  items:
    | CommandPickerItem[]
    | ((editor: Editor) => Promise<CommandPickerItem[]>);
  suggestionProps: SuggestionProps;
}
export interface CommandPickerPopoverRef {
  update: (nextProps: SuggestionProps) => void;
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}
const isGroup = (item: CommandPickerItem): item is CommandPickerGroupItem => {
  return 'items' in item;
};
const getMenuItems = (items: CommandPickerItem[]): ItemType[] => {
  return items.map((item) =>
    isGroup(item)
      ? {
          type: 'group',
          key: item.id,
          label: item.label,
          children: getMenuItems(item.items),
        }
      : {
          id: item.id,
          key: item.id,
          label: item.label,
          icon: item.icon,
        },
  );
};
const filterItems = (items: CommandPickerItem[], searchText: string) => {
  return items
    .map((item) => {
      if (isGroup(item)) {
        const isGroupMatch = item.label.toLowerCase().includes(searchText);

        if (isGroupMatch) {
          return item;
        }
        const filteredItems = filterItems(item.items, searchText).filter(
          Boolean,
        );

        if (filteredItems.length > 0) {
          return {
            ...item,
            items: filteredItems,
          };
        }

        return null;
      }

      return item.label.toLowerCase().includes(searchText) ? item : null;
    })
    .filter(Boolean);
};
const filterAllowedItems = (
  items: CommandPickerItem[],
  editor: Editor,
  range: Range,
) => {
  return items
    .map((item) => {
      if (isGroup(item)) {
        return {
          ...item,
          items: filterAllowedItems(item.items, editor, range),
        };
      }

      return item;
    })
    .filter((item) => {
      if (isGroup(item)) {
        return item.items.length > 0;
      }

      return item.isAllowed?.(editor, range) ?? true;
    });
};
const Menu = styled(MenuAtom)`
  max-height: unset;
`;
const Wrapper = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

interface ItemState {
  items: CommandPickerItem[];
  isLoading: boolean;
}
const itemsLoaded = (items: CommandPickerItem[]): ItemState => {
  return {
    items,
    isLoading: false,
  };
};
const itemsLoading = (): ItemState => {
  return {
    items: [],
    isLoading: true,
  };
};

export const CommandsPickerPopover = forwardRef<
  CommandPickerPopoverRef,
  CommandsPickerProps
>(
  (
    {
      editor,
      popoverState,
      suggestionProps: initialSuggestionProps,
      items: itemsOrItemsGetter,
      ...popoverProps
    },
    ref,
  ) => {
    const [suggestionProps, setSuggestionProps] = useState<SuggestionProps>(
      initialSuggestionProps,
    );
    const [itemsState, setItemsState] = useState<ItemState>({
      items: [],
      isLoading: false,
    });
    const latestRequestIdRef = useRef<number>(0);

    useEffect(() => {
      if (typeof itemsOrItemsGetter === 'function') {
        setItemsState(itemsLoading());
        const currentRequestId = latestRequestIdRef.current + 1;

        latestRequestIdRef.current = currentRequestId;
        itemsOrItemsGetter(editor)
          .then((items) => {
            if (currentRequestId === latestRequestIdRef.current) {
              setItemsState(itemsLoaded(items));
            }
          })
          .catch(() => {
            if (currentRequestId === latestRequestIdRef.current) {
              setItemsState(itemsLoaded([]));
            }
          });
      } else {
        setItemsState(itemsLoaded(itemsOrItemsGetter));
      }

      return () => {
        latestRequestIdRef.current = -1;
      };
    }, [itemsOrItemsGetter, editor]);
    // TODO: optimize this to avoid re-rendering the items
    const allowedItems = useMemo(() => {
      return filterAllowedItems(
        itemsState.items,
        editor,
        suggestionProps.range,
      );
    }, [itemsState.items, editor, suggestionProps.range]);
    const filteredItems = useMemo(() => {
      const searchText = suggestionProps.query.toLowerCase().trim();

      return filterItems(allowedItems, searchText);
    }, [allowedItems, suggestionProps.query]);
    const flattedItems = useMemo(() => {
      return flatMap(filteredItems, (item) =>
        isGroup(item) ? item.items : item,
      );
    }, [filteredItems]);
    const [activeItemIndex, setActiveItemIndex] = useState<number>(0);

    useEffect(() => {
      setActiveItemIndex(0);
    }, [filteredItems, suggestionProps.query]);

    useImperativeHandle(ref, () => ({
      update: setSuggestionProps,
      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (flattedItems.length === 0) {
          return false;
        }
        switch (props.event.key) {
          case 'ArrowDown':
            setActiveItemIndex(
              (previous) => (previous + 1) % flattedItems.length,
            );

            return true;
          case 'ArrowUp':
            setActiveItemIndex((previous) =>
              previous - 1 < 0 ? flattedItems.length - 1 : previous - 1,
            );

            return true;
          case 'Enter':
            suggestionProps.command(flattedItems[activeItemIndex]);

            return true;
        }

        return false;
      },
    }));
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!wrapperRef.current) {
        return;
      }

      const activeItem = wrapperRef.current.querySelector(
        `.ant-menu-item[id="${flattedItems[activeItemIndex]?.id}"]`,
      );

      if (activeItem) {
        activeItem.scrollIntoView({
          block: 'nearest',
          behavior: 'instant',
        });
      }
    }, [activeItemIndex, flattedItems]);
    const Content = useMemo(() => {
      return (
        <Wrapper ref={wrapperRef}>
          {itemsState.isLoading ? (
            <Loading />
          ) : (
            <Menu
              items={getMenuItems(filteredItems)}
              mode="inline"
              onClick={({ key }) => {
                suggestionProps.command(
                  flattedItems.find((item) => item.id === key),
                );
              }}
              selectedKeys={[flattedItems[activeItemIndex]?.id]}
            />
          )}
        </Wrapper>
      );
    }, [
      filteredItems,
      activeItemIndex,
      flattedItems,
      suggestionProps,
      itemsState.isLoading,
    ]);

    return (
      <EditorPopover
        editor={editor}
        popoverState={
          popoverState.visible && flattedItems.length === 0
            ? { visible: false }
            : popoverState
        }
        content={() => Content}
        {...popoverProps}
      />
    );
  },
);

CommandsPickerPopover.displayName = 'CommandsPickerPopover';
